/* ============================================================
   EXCLUSIVE OFFERS — exclusive-offers.js
   Pure vanilla JS — no dependencies.
   Features:
     • Touch / swipe support
     • Auto-slide (configurable speed)
     • Pagination dots
     • 2 cards on mobile, 2 on tablet, 4 on desktop
     • Add to Cart via Shopify AJAX API
     • Multiple section instances supported
   ============================================================ */

(function () {
  'use strict';

  /* ── Helper: how many cards fit in the viewport ── */
  function getVisibleCount() {
    if (window.innerWidth <= 600) return 2;
    if (window.innerWidth <= 900) return 2;
    return 4;
  }

  /* ══════════════════════════════════════════
     SLIDER CLASS
     ══════════════════════════════════════════ */
  class EOSlider {
    constructor(section) {
      this.section   = section;
      this.track     = section.querySelector('.eo-track');
      this.dotsWrap  = section.querySelector('.eo-dots');
      this.cards     = Array.from(section.querySelectorAll('.eo-card'));
      this.autoSlide = section.dataset.autoSlide !== 'false';
      this.speed     = parseInt(section.dataset.autoSlideSpeed, 10) || 3500;

      this.current   = 0;
      this.timer     = null;
      this.dragging  = false;
      this.dragStart = 0;
      this.dragDelta = 0;

      if (!this.track || this.cards.length === 0) return;

      this._buildDots();
      this._bindEvents();
      this._goto(0);

      if (this.autoSlide) this._startTimer();
    }

    /* ── Page count ── */
    get pageCount() {
      return Math.ceil(this.cards.length / getVisibleCount());
    }

    /* ── Build pagination dots ── */
    _buildDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dots = [];

      for (let i = 0; i < this.pageCount; i++) {
        const btn = document.createElement('button');
        btn.className = 'eo-dots__dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', `Page ${i + 1}`);
        btn.addEventListener('click', () => {
          this._goto(i);
          this._resetTimer();
        });
        this.dotsWrap.appendChild(btn);
        this.dots.push(btn);
      }
    }

    /* ── Slide to page ── */
    _goto(page) {
      const visible    = getVisibleCount();
      const cardWidth  = this.cards[0]?.offsetWidth || 0;
      const gap        = 16; /* matches CSS gap */
      const offset     = page * visible * (cardWidth + gap);

      this.current = page;
      this.track.style.transform = `translateX(-${offset}px)`;
      this._updateDots();
    }

    /* ── Update dot active state ── */
    _updateDots() {
      if (!this.dots) return;
      this.dots.forEach((d, i) => {
        d.classList.toggle('active', i === this.current);
        d.setAttribute('aria-selected', i === this.current ? 'true' : 'false');
      });
    }

    /* ── Next / Prev ── */
    _next() {
      const next = (this.current + 1) % this.pageCount;
      this._goto(next);
    }

    _prev() {
      const prev = (this.current - 1 + this.pageCount) % this.pageCount;
      this._goto(prev);
    }

    /* ── Auto-slide timer ── */
    _startTimer() {
      this.timer = setInterval(() => this._next(), this.speed);
    }

    _stopTimer() {
      if (this.timer) clearInterval(this.timer);
    }

    _resetTimer() {
      this._stopTimer();
      if (this.autoSlide) this._startTimer();
    }

    /* ── Bind all events ── */
    _bindEvents() {
      /* Touch */
      this.track.addEventListener('touchstart', (e) => this._onDragStart(e.touches[0].clientX), { passive: true });
      this.track.addEventListener('touchmove',  (e) => this._onDragMove(e.touches[0].clientX),  { passive: true });
      this.track.addEventListener('touchend',   ()  => this._onDragEnd());

      /* Mouse drag */
      this.track.addEventListener('mousedown',  (e) => this._onDragStart(e.clientX));
      this.track.addEventListener('mousemove',  (e) => { if (this.dragging) this._onDragMove(e.clientX); });
      this.track.addEventListener('mouseup',    ()  => this._onDragEnd());
      this.track.addEventListener('mouseleave', ()  => { if (this.dragging) this._onDragEnd(); });

      /* Pause on hover */
      this.section.addEventListener('mouseenter', () => this._stopTimer());
      this.section.addEventListener('mouseleave', () => { if (this.autoSlide) this._startTimer(); });

      /* Keyboard */
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { this._prev(); this._resetTimer(); }
        if (e.key === 'ArrowRight') { this._next(); this._resetTimer(); }
      });

      /* Rebuild on resize (card count may change) */
      window.addEventListener('resize', this._debounce(() => {
        this._buildDots();
        this._goto(0);
      }, 200));
    }

    _onDragStart(x) {
      this.dragging  = true;
      this.dragStart = x;
      this.dragDelta = 0;
      this._stopTimer();
    }

    _onDragMove(x) {
      if (!this.dragging) return;
      this.dragDelta = x - this.dragStart;
    }

    _onDragEnd() {
      if (!this.dragging) return;
      this.dragging = false;
      const threshold = 50;
      if (this.dragDelta < -threshold) this._next();
      else if (this.dragDelta > threshold) this._prev();
      if (this.autoSlide) this._startTimer();
    }

    /* ── Debounce utility ── */
    _debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  /* ══════════════════════════════════════════
     ADD TO CART (Shopify AJAX)
     ══════════════════════════════════════════ */
  function bindAddToCart(section) {
    section.querySelectorAll('.eo-btn[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        e.preventDefault();

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        const originalText = btn.textContent;
        btn.textContent = '...';
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ id: variantId, quantity: 1 }),
          });

          if (!res.ok) throw new Error('Cart error');

          btn.textContent = '✓ Added';
          btn.style.background = 'var(--eo-accent-dark)';

          /* Trigger cart update event for theme drawer/cart */
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        } catch (err) {
          btn.textContent = 'Error';
          console.error('[Exclusive Offers] Add to cart failed:', err);
        } finally {
          setTimeout(() => {
            btn.textContent = originalText;
            btn.style.background = '';
            btn.disabled = false;
          }, 2000);
        }
      });
    });
  }

  /* ══════════════════════════════════════════
     INIT — supports multiple sections on page
     ══════════════════════════════════════════ */
  function init() {
    document.querySelectorAll('.eo-section').forEach((section) => {
      /* Skip already-initialised sections */
      if (section._eoSlider) return;
      section._eoSlider = new EOSlider(section);
      bindAddToCart(section);
    });
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor live reload support */
  document.addEventListener('shopify:section:load',   init);
  document.addEventListener('shopify:section:reorder', init);

})();y